import { useRef, useState } from 'react';
import {
  adminDeletionRequestDecline,
  adminDeletionRequestExecute,
  adminDeletionRequestLog,
  adminDeletionRequests,
  adminUserDelete,
  adminUserUpdate,
  facilityDeletionRequestDecline,
  facilityDeletionRequestExecute,
  facilityDeletionRequests,
  type ApiError,
  type DeletionRequest,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ErrLine, I, btn, pre, sx, useApi, useGuarded } from './kit';

// Spec V4 step 3 (V4 T9122 and T9238, settingsVals L23595-23660): the Account deletion
// requests block under Access requests, and the Account row at the end of User detail.

const KIND_LABEL: Record<string, string> = {
  family: 'Family', home_member: 'Aithora Home', facility_staff: 'Staff', resident: 'Resident', team: 'Aithora team', owner: 'Owner', unknown: 'Account',
};

/** The design's small action button (smallBtn, L23620): live blue or red, else greyed. */
const smallBtn = (on: boolean, red: boolean) =>
  'display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
  (on ? (red ? 'background:#B3261E;color:#fff;border:1px solid #B3261E;' : 'background:#2F5C97;color:#fff;border:1px solid #2F5C97;') : 'background:rgba(34,39,46,.06);color:#54606C;border:1px solid rgba(34,39,46,.14);cursor:not-allowed;');
const INPUT = 'flex:1 1 240px;min-width:0;height:32px;padding:0 11px;border:1px solid rgba(34,39,46,.18);border-radius:8px;background:#FBF8F1;font:inherit;font-size:16px;color:#22272E;box-sizing:border-box;';
const STATE_TONE: Record<string, string> = { active: '#1F6B52', suspended: '#8A5A00', deleted: '#8A1C16' };

/** A user's state as the design colours it (stateStyle / stateDot, L23560). */
export const stateStyle = (s: string) => 'display:inline-flex;align-items:center;gap:6px;font-size:16px;flex:0 1 auto;min-width:0;color:' + (STATE_TONE[s] ?? '#54606C') + ';';
export const stateDot = (s: string) => 'width:6px;height:6px;border-radius:50%;background:' + (STATE_TONE[s] ?? '#54606C') + ';';

const ago = (iso: string): string => {
  const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (m < 60) return m <= 1 ? 'just now' : m + 'm ago';
  const h = Math.round(m / 60);
  if (h < 24) return h + 'h ago';
  const d = Math.round(h / 24);
  return d === 1 ? 'yesterday' : d + ' days ago';
};

/** "today", "yesterday" or the date: how a building says when a resident asked (V4 L23612). */
const dayWord = (iso: string): string => {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'today';
  if (new Date(now.getTime() - 86_400_000).toDateString() === d.toDateString()) return 'yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

interface Row {
  id: string;
  head: string;
  meta: string;
  note: string;
  accts: { userId: string; label: string }[];
}
interface Closed { state: 'DELETED' | 'DECLINED'; result: string; row: Row }

/**
 * Account deletion requests. Aithora sees what came to support@ and the public delete-account
 * page (GET /admin/account-deletion/requests, with the accounts that hold that email); a
 * Facility Admin sees their own residents' requests (GET /account/deletion-requests). Pick
 * the account, say how you confirmed the requester controls the email, then Delete; or
 * Decline with the reason the requester is sent. Both outcomes are sealed on the server.
 */
export function DeletionRequestsBlock({ facility, orgName, onChanged }: { facility: boolean; orgName: string; onChanged: () => void }) {
  const { client } = useSession();
  const { wr } = useGuarded();
  const admin = useApi((c, s) => adminDeletionRequests(c, { query: { status: 'received' }, signal: s }), [], !facility);
  const fac = useApi((c, s) => facilityDeletionRequests(c, { signal: s }), [], facility);
  const q = facility ? fac : admin;
  const [draft, setDraft] = useState<Record<string, { pick?: number; how?: string; why?: string; declining?: boolean }>>({});
  const [closed, setClosed] = useState<Record<string, Closed>>({});
  const [err, setErr] = useState<ApiError | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  const [logEmail, setLogEmail] = useState('');

  const toRow = (r: DeletionRequest): Row => {
    const channel = r.channel === 'email' ? 'Email to support@' : r.channel === 'web' ? 'Public delete-account page' : facility ? 'asked in Atlas' : 'In the app';
    const matches = r.matches ?? [];
    return {
      id: r.id,
      head: facility ? r.residentName ?? r.name ?? 'Resident' : r.email ?? r.residentName ?? 'Request',
      meta: (facility ? [KIND_LABEL[r.accountKind ?? 'resident'] ?? 'Resident', channel, dayWord(r.createdAt)] : [channel, ago(r.createdAt)]).join(' · '),
      note: r.details ?? '',
      accts: matches.map((m) => ({
        userId: m.userId,
        label: [(m as { name?: string | null }).name ?? r.name ?? r.email ?? 'Account', KIND_LABEL[m.kind] ?? m.kind, m.organizationName].filter(Boolean).join(' · '),
      })),
    };
  };
  // Rows keep the place they first came in, so a decided request stays where it was (V4).
  const order = useRef<string[]>([]);
  const fetched = (q.data?.requests ?? []).map(toRow);
  fetched.forEach((r) => { if (!order.current.includes(r.id)) order.current.push(r.id); });
  const byId = new Map(fetched.map((r) => [r.id, r] as const));
  const items = order.current
    .map((id) => (closed[id] ? { id, done: closed[id] as Closed | null, row: null as Row | null } : byId.has(id) ? { id, done: null, row: byId.get(id)! } : null))
    .filter((x): x is { id: string; done: Closed | null; row: Row | null } => x !== null);
  const live = items.filter((x) => x.row).map((x) => x.row!);
  const done = Object.values(closed);
  const openN = live.length;
  const set = (id: string, patch: (typeof draft)[string]) => setDraft((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const doDelete = async (r: Row, pick: number, how: string) => {
    setErr(null);
    try {
      const a = r.accts[pick];
      const out = facility
        ? await facilityDeletionRequestExecute(client, { params: { id: r.id }, body: { verification: how, confirm: 'DELETE' } })
        : await adminDeletionRequestExecute(client, { params: { id: r.id }, body: { userId: a.userId, verification: how, confirm: 'DELETE' } });
      const forwarded = (out as { forwarded?: boolean }).forwarded;
      setClosed((c) => ({ ...c, [r.id]: { row: r, state: 'DELETED', result: forwarded ? a.label + ': sent to their facility, who decide. On the Truth Ledger.' : a.label + ' deleted. Confirmed: ' + how + '. On the Truth Ledger.' } }));
      onChanged();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  const doDecline = async (r: Row, why: string) => {
    setErr(null);
    try {
      if (facility) await facilityDeletionRequestDecline(client, { params: { id: r.id }, body: { reason: why } });
      else await adminDeletionRequestDecline(client, { params: { id: r.id }, body: { reason: why } });
      setClosed((c) => ({ ...c, [r.id]: { row: r, state: 'DECLINED', result: 'Declined: ' + why + '. The requester was told.' } }));
      onChanged();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  const logIt = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(logEmail.trim())) return;
    setErr(null);
    try {
      await adminDeletionRequestLog(client, { body: { email: logEmail.trim() } });
      setLogEmail('');
      setLogOpen(false);
      admin.reload();
    } catch (e) {
      setErr(e as ApiError);
    }
  };

  const stateChip = (s: string) =>
    'display:inline-flex;align-items:center;height:20px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;' +
    (s === 'DELETED' ? 'color:#12503C;background:rgba(31,107,82,.12);' : s === 'DECLINED' ? 'color:#54606C;background:rgba(34,39,46,.07);' : 'color:#8A1C16;background:rgba(179,38,30,.1);');

  return (
    <div data-screen-label="Account deletion requests" data-testid="deletion-requests" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;margin-bottom:12px;')}>
      <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#B3261E" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={sx('flex:0 0 15px;')} aria-hidden="true">
          <circle cx="9" cy="8" r="3.4" />
          <path d="M2.8 20a6.2 6.2 0 0 1 12.4 0" />
          <path d="M16.5 8h5" />
        </svg>
        <span style={sx('font-size:16px;font-weight:700;')}>Account deletion requests</span>
        <span style={sx('display:inline-flex;align-items:center;height:20px;padding:0 8px;border-radius:5px;font-size:16px;font-weight:700;white-space:nowrap;' + (openN ? 'color:#8A1C16;background:rgba(179,38,30,.1);' : 'color:#54606C;background:rgba(34,39,46,.06);'))}>
          {I(openN ? openN + ' open' : 'none open')}
        </span>
        {/* Emailed requests are logged here (the design draws no control for it); it sits
            left of the spacer so the design's own row keeps its places. */}
        {!facility && (
          <a href="#" aria-expanded={logOpen} {...wr(() => setLogOpen(!logOpen))} style={sx('font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}>
            {logOpen ? 'Cancel' : 'Log an emailed request'}
          </a>
        )}
        <span style={sx('flex:1;')} />
        <span style={sx('font-size:16px;color:#54606C;')}>{I(facility ? 'Residents of ' + orgName + ' ask here' : 'From support@ and the public delete-account page')}</span>
      </div>
      {logOpen && (
        <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-top:9px;')}>
          <input type="email" aria-label="Requester email" value={logEmail} onChange={(e) => setLogEmail(e.target.value)} placeholder="The email the request came from" style={sx(INPUT)} />
          <a href="#" {...wr(() => void logIt())} style={sx(smallBtn(/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(logEmail.trim()), false))}>Log request</a>
        </div>
      )}
      {q.error && !q.data && <div style={sx('font-size:16px;color:#54606C;margin-top:9px;')}>Account deletion requests could not be loaded.</div>}
      {q.data && live.length === 0 && done.length === 0 && <div style={sx('font-size:16px;color:#54606C;margin-top:9px;')}>No deletion requests.</div>}
      {items.map((it) => {
        if (it.done) {
          const c = it.done;
          return (
            <div key={c.row.id} style={sx('display:flex;flex-direction:column;gap:7px;padding:10px 0;border-bottom:1px solid rgba(34,39,46,.07);min-width:0;')}>
              <div style={sx('display:flex;align-items:baseline;gap:6px 12px;flex-wrap:wrap;min-width:0;')}>
                <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{I(c.row.head)}</span>
                <span style={sx('font-size:16px;color:#54606C;')}>{I(c.row.meta)}</span>
                <span style={sx('flex:1 1 20px;')} />
                <span style={sx(stateChip(c.state))}>{c.state}</span>
              </div>
              {c.row.note && <div style={sx('font-size:16px;color:#3C454F;line-height:1.5;text-wrap:pretty;')}>{'“'}{I(c.row.note)}{'”'}</div>}
              <div style={sx('font-size:16px;color:#3C454F;')}>{I(c.result)}</div>
            </div>
          );
        }
        const r = it.row!;
        const d = draft[r.id] ?? {};
        const pick = d.pick === undefined ? (r.accts.length === 1 ? 0 : -1) : d.pick;
        const how = d.how ?? '';
        const why = d.why ?? '';
        const canDel = pick >= 0 && how.trim().length > 2;
        const canDecline = why.trim().length > 2;
        return (
          <div key={r.id} style={sx('display:flex;flex-direction:column;gap:7px;padding:10px 0;border-bottom:1px solid rgba(34,39,46,.07);min-width:0;')}>
            <div style={sx('display:flex;align-items:baseline;gap:6px 12px;flex-wrap:wrap;min-width:0;')}>
              <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{I(r.head)}</span>
              <span style={sx('font-size:16px;color:#54606C;')}>{I(r.meta)}</span>
              <span style={sx('flex:1 1 20px;')} />
              <span style={sx(stateChip('OPEN'))}>OPEN</span>
            </div>
            {r.note && <div style={sx('font-size:16px;color:#3C454F;line-height:1.5;text-wrap:pretty;')}>{'“'}{I(r.note)}{'”'}</div>}
            <div style={sx('display:flex;align-items:center;gap:6px 8px;flex-wrap:wrap;')}>
              <span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#54606C;')}>Accounts with this email</span>
              {r.accts.map((a, j) => (
                <a
                  key={a.userId}
                  href="#"
                  aria-pressed={pick === j}
                  onClick={(e) => { pre(e); set(r.id, { pick: j }); }}
                  style={sx('display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:6px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' + (pick === j ? 'background:rgba(47,92,151,.12);color:#264C7E;border:1px solid rgba(47,92,151,.45);' : 'background:#F7F3EA;color:#22272E;border:1px solid rgba(34,39,46,.2);'))}
                >
                  {I(a.label)}
                </a>
              ))}
              {r.accts.length === 0 && <span style={sx('font-size:16px;color:#54606C;')}>No account has this email.</span>}
            </div>
            <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;')}>
              <input type="text" aria-label="How you confirmed they control this email" value={how} onChange={(e) => set(r.id, { how: e.target.value })} placeholder="How you confirmed they control this email" style={sx(INPUT)} />
              <a href="#" {...wr(() => { if (canDel) void doDelete(r, pick, how.trim()); })} aria-disabled={!canDel || undefined} style={sx(smallBtn(canDel, true))}>
                {pick >= 0 ? 'Delete this account' : 'Pick an account'}
              </a>
              <a href="#" aria-expanded={!!d.declining} {...wr(() => set(r.id, { declining: !d.declining }))} style={sx('display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#3C454F;border:1px solid rgba(34,39,46,.2);')}>
                Decline
              </a>
            </div>
            {d.declining && (
              <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;')}>
                <input type="text" aria-label="Reason, sent to the requester" value={why} onChange={(e) => set(r.id, { why: e.target.value })} placeholder="Reason, sent to the requester" style={sx(INPUT)} />
                <a href="#" {...wr(() => { if (canDecline) void doDecline(r, why.trim()); })} aria-disabled={!canDecline || undefined} style={sx(smallBtn(canDecline, false))}>Send decline</a>
              </div>
            )}
            {(pick < 0 || !how.trim()) && <div style={sx('font-size:16px;color:#54606C;')}>{I(pick < 0 ? 'Pick which account to delete.' : 'Say how you confirmed it: for example, a reply from that email.')}</div>}
          </div>
        );
      })}
      <ErrLine error={err} style="margin-top:6px;" />
    </div>
  );
}

/**
 * User detail's Account row: Suspend / Resume, and once suspended, type DELETE to run the
 * full section-15 deletion. Refused here and on the server: the owner account, a building's
 * last active Admin, and any Aithora team account unless the viewer is the owner.
 */
export function AccountBlock({
  user,
  facility,
  orgName,
  activeAdmins,
  viewerOwner,
  teamAccount,
  onChanged,
}: {
  user: { id: string; name: string; status: string; role: string; founder?: boolean; pill: string | null };
  facility: boolean;
  orgName: string;
  activeAdmins: number;
  viewerOwner: boolean;
  /** An Aithora team account (Super Admin, Operator, Sales, Developer, Partner). */
  teamAccount: boolean;
  onChanged: () => void;
}) {
  const { client } = useSession();
  const { wr } = useGuarded();
  const [typed, setTyped] = useState('');
  const [err, setErr] = useState<ApiError | null>(null);
  const state = user.status === 'deleted' ? 'deleted' : user.status === 'suspended' ? 'suspended' : 'active';
  const lastAdmin = facility && user.role === 'ADMIN' && activeAdmins <= 1;
  const teamLocked = !facility && teamAccount && !viewerOwner;
  const block = user.founder
    ? 'The owner account can’t be suspended or deleted.'
    : lastAdmin
      ? orgName + '’s last active Admin. Add another Admin before this account can be removed.'
      : teamLocked
        ? 'Only Andrew removes Aithora team accounts.'
        : '';
  const ok = typed.trim().toUpperCase() === 'DELETE';
  const note = state === 'deleted'
    ? 'Deleted. Only the name stays, as author of what they wrote. Recorded on the Truth Ledger.'
    : block || (state === 'suspended' ? 'Suspended: signed out everywhere and cannot sign in. Delete removes the account for good.' : 'Suspend first. Delete is offered once the account is suspended.');
  const run = async (fn: () => Promise<unknown>) => {
    setErr(null);
    try {
      await fn();
      setTyped('');
      onChanged();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  return (
    <div data-screen-label="Suspend and delete" data-testid="account-block" style={sx('margin-top:14px;padding-top:12px;border-top:1px solid rgba(34,39,46,.1);')}>
      <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:8px;')}>Account</div>
      <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
        <span style={sx('font-size:16px;color:#3C454F;line-height:1.5;min-width:min(100%,150px);flex:1 1 150px;text-wrap:pretty;')}>{I(note)}</span>
        {!block && state !== 'deleted' && (
          <a href="#" {...wr(() => void run(() => adminUserUpdate(client, { params: { id: user.id }, body: { status: state === 'suspended' ? 'active' : 'suspended' } })))} style={sx(btn('ghost'))}>
            {state === 'suspended' ? 'Resume' : 'Suspend'}
          </a>
        )}
      </div>
      {!block && state === 'suspended' && (
        <>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-top:9px;')}>
            <input type="text" aria-label="Type DELETE" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type DELETE" style={sx('flex:1 1 140px;min-width:0;height:32px;padding:0 11px;border:1px solid rgba(34,39,46,.18);border-radius:8px;background:#FBF8F1;font:inherit;font-size:14px;font-weight:600;letter-spacing:.05em;color:#22272E;box-sizing:border-box;')} />
            <a href="#" {...wr(() => { if (ok) void run(() => adminUserDelete(client, { params: { id: user.id } })); })} aria-disabled={!ok || undefined} style={sx(smallBtn(ok, true))}>
              Delete account
            </a>
          </div>
          <div style={sx('font-size:16px;color:#54606C;line-height:1.5;margin-top:6px;text-wrap:pretty;')}>
            Full deletion, the same as Delete my account: sign-in, sessions, phones and settings go; open work goes back to the team; the records they wrote keep their name.
          </div>
        </>
      )}
      <ErrLine error={err} style="margin-top:6px;" />
    </div>
  );
}
